import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Banknote,
  CalendarRange,
  CheckCircle2,
  HardHat,
  Play,
  RefreshCw,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { PageHead } from '../components/bits.jsx';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  Field,
  Input,
  Loading,
  Modal,
  Stat,
  useToast,
} from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { compactMoney, dateLabel, money, num, titleCase } from '../lib/format.js';

export default function Payroll() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: runs, loading, refresh } = useApi('/payroll/runs');
  const { data: summary, refresh: refreshSummary } = useApi('/payroll/summary');
  const { data: payouts } = useApi('/payroll/payouts', { params: { limit: 60 } });
  const [buildOpen, setBuildOpen] = useState(false);

  const list = runs || [];
  const pending = list.filter((run) => run.status !== 'paid');

  return (
    <>
      <PageHead
        title="Paying lumpers"
        subtitle="Work out what everyone earned, check it, then pay them."
        icon={Banknote}
        actions={
          <>
            <Button variant="outline" size="sm" icon={RefreshCw} onClick={() => { refresh(); refreshSummary(); }}>Refresh</Button>
            <Button variant="primary" size="sm" icon={Play} onClick={() => setBuildOpen(true)}>Work out the week\u2019s pay</Button>
          </>
        }
      />

      <div className="grid grid-4 mb-20">
        <Stat label="Waiting to be paid" icon={Wallet} value={compactMoney(summary?.readyToSettle?.amount)} tone="amber" foot={`${num(summary?.readyToSettle?.jobs)} completed jobs`} />
        <Stat label="Ready to send" icon={Banknote} value={compactMoney(summary?.pending?.amount)} tone="blue" foot={`${num(summary?.pending?.lumpers)} lumpers waiting`} />
        <Stat label="Paid out in 30 days" icon={CheckCircle2} value={compactMoney(summary?.paid30?.amount)} tone="green" foot={`${num(summary?.paid30?.runs)} pay runs`} />
        <Stat label="Usual pay per job" icon={TrendingUp} value={money(summary?.avgPerJob?.amount)} tone="violet" foot="lumper pay" />
      </div>

      {pending.length ? (
        <Callout tone="amber" icon={CalendarRange} title={`${pending.length} pay run${pending.length > 1 ? 's' : ''} waiting on approval`} className="mb-20">
          Review the per-lumper breakdown, adjust if needed, then approve and pay. Work only moves to “paid” once the run is paid.
        </Callout>
      ) : null}

      <div className="split-wide mb-20">
        <Card>
          <CardHead title="Pay runs" subtitle="Most recent first" icon={CalendarRange} />
          {loading && !list.length ? (
            <Loading rows={5} />
          ) : list.length ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Pay run</th>
                    <th className="narrow">Which days</th>
                    <th className="narrow">Status</th>
                    <th className="right">People</th>
                    <th className="right">Jobs</th>
                    <th className="right">Earned</th>
                    <th className="right">Charges</th>
                    <th className="right">They get</th>
                    <th className="narrow">Paid on</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((run) => (
                    <tr key={run.id} className="clickable" onClick={() => navigate(`/app/payroll/${run.id}`)}>
                      <td>
                        <div className="col gap-2">
                          <span className="mono" style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{run.code}</span>
                          {run.approvedBy ? <span className="dimmer" style={{ fontSize: 10 }}>approved by {run.approvedBy}</span> : null}
                        </div>
                      </td>
                      <td className="narrow dim">{dateLabel(run.periodStart)} → {dateLabel(run.periodEnd)}</td>
                      <td className="narrow">
                        <Badge tone={run.status === 'paid' ? 'green' : run.status === 'approved' ? 'teal' : 'amber'} size="sm">
                          {titleCase(run.status)}
                        </Badge>
                      </td>
                      <td className="right num">{run.lumpers}</td>
                      <td className="right num">{run.jobsCount}</td>
                      <td className="right num">{money(run.gross)}</td>
                      <td className="right num">{money(run.fees)}</td>
                      <td className="right num" style={{ fontWeight: 640 }}>{money(run.net)}</td>
                      <td className="narrow dim">{run.payDate ? dateLabel(run.payDate) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState icon={Banknote} title="No pay runs yet">
              Complete some work, then build a run for the period.
            </EmptyState>
          )}
        </Card>

        <div className="col gap-20">
          <Card>
            <CardHead title="Who earned the most" icon={HardHat} />
            <CardBody className="col gap-12">
              {(summary?.topEarners || []).map((lumper) => (
                <Link key={lumper.id} to={`/app/lumpers/${lumper.id}`} className="row gap-10">
                  <Avatar name={lumper.name} tone={lumper.photoColor || 'amber'} size="sm" />
                  <div className="grow truncate">
                    <div className="truncate" style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{lumper.name}</div>
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{lumper.jobs} jobs · {titleCase(lumper.tier)}</div>
                  </div>
                  <span className="num" style={{ fontSize: 'var(--fs-sm)' }}>{money(lumper.amount)}</span>
                </Link>
              ))}
              {!summary?.topEarners?.length ? <span className="dim">No paid work in the window.</span> : null}
            </CardBody>
          </Card>

          <Card>
            <CardHead title="Last payments" subtitle="What each person was paid" icon={Wallet} />
            <CardBody className="col gap-10" style={{ maxHeight: 320, overflow: 'auto' }}>
              {(payouts || []).slice(0, 14).map((payout) => (
                <div key={payout.id} className="row between gap-10">
                  <span className="row gap-8 truncate">
                    <Avatar name={payout.lumperName} tone={payout.lumperPhotoColor || 'blue'} size="sm" />
                    <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>{payout.lumperName}</span>
                  </span>
                  <span className="row gap-8">
                    <Badge tone={payout.status === 'paid' ? 'green' : 'amber'} size="sm">{payout.status}</Badge>
                    <span className="num" style={{ fontSize: 'var(--fs-sm)' }}>{money(payout.net)}</span>
                  </span>
                </div>
              ))}
              {!payouts?.length ? <span className="dim">No payouts recorded.</span> : null}
            </CardBody>
          </Card>
        </div>
      </div>

      <BuildRunModal
        open={buildOpen}
        onClose={() => setBuildOpen(false)}
        onBuilt={(run) => {
          toast.ok(`${run.code} built`, `${run.jobsCount} jobs across ${run.lumpers} lumpers — ${money(run.net)}`);
          refresh();
          refreshSummary();
          navigate(`/app/payroll/${run.id}`);
        }}
      />
    </>
  );
}

function BuildRunModal({ open, onClose, onBuilt }) {
  const toast = useToast();
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 864e5);
  const [from, setFrom] = useState(weekAgo.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const { data: preview } = useApi('/payroll/preview', {
    params: { periodStart: new Date(from).toISOString(), periodEnd: new Date(`${to}T23:59:59`).toISOString() },
    deps: [from, to],
    skip: !open,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="wide"
      title="Build a pay run"
      subtitle="Every completed or verified job in the period lands in the run"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!preview?.lumpers?.length}
            onClick={async () => {
              setBusy(true);
              try {
                const payload = await api.post('/payroll/runs', {
                  periodStart: new Date(from).toISOString(),
                  periodEnd: new Date(`${to}T23:59:59`).toISOString(),
                  notes,
                });
                onBuilt(payload.data);
                onClose();
              } catch (err) {
                toast.err('Could not build the run', err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Build run
          </Button>
        </>
      }
    >
      <div className="col gap-16">
        <div className="grid grid-2" style={{ gap: 14 }}>
          <Field label="Period start"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="Period end"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        </div>

        {preview?.lumpers?.length ? (
          <>
            <div className="grid grid-3">
              <div className="tile">
                <span className="label">Lumpers</span>
                <div className="stat-value sm">{num(preview.totals.lumpers)}</div>
              </div>
              <div className="tile">
                <span className="label">Jobs</span>
                <div className="stat-value sm">{num(preview.totals.jobs)}</div>
              </div>
              <div className="tile">
                <span className="label">Net payout</span>
                <div className="stat-value sm">{money(preview.totals.net)}</div>
              </div>
            </div>
            <div className="table-wrap" style={{ maxHeight: 280 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Lumper</th>
                    <th className="right">Jobs</th>
                    <th className="right">Hours</th>
                    <th className="right">Gross</th>
                    <th className="right">Fees</th>
                    <th className="right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lumpers.map((lumper) => (
                    <tr key={lumper.lumperId}>
                      <td className="truncate">{lumper.name}</td>
                      <td className="right num">{lumper.jobs.length}</td>
                      <td className="right num">{Number(lumper.hours).toFixed(1)}</td>
                      <td className="right num">{money(lumper.gross)}</td>
                      <td className="right num">{money(lumper.fees)}</td>
                      <td className="right num" style={{ fontWeight: 620 }}>{money(lumper.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <EmptyState icon={Banknote} title="Nothing to settle in that window">
            Widen the dates, or verify completed work first.
          </EmptyState>
        )}

        <Field label="Notes for the run"><Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Includes $75 reliability bonus…" /></Field>
      </div>
    </Modal>
  );
}
